import { FormGroup } from "@angular/forms";
import { compare, satisfies } from "compare-versions";
import { Injectable, isSignal } from "@angular/core";
import { v4 as uuid } from "uuid";
import {
  querySelectorAllDeep,
  querySelectorDeep,
} from "query-selector-shadow-dom";
import { SafeHtml, SafeStyle } from "@angular/platform-browser";
import { ScriptRepository } from "../repositories/scripts.repository";
import { EventService } from "./event.service";
import { NetworkService } from "./network.service";
import { FilterService } from "./filter.service";
import { NavigationService } from "./navigation.service";
import { DataService } from "./data.service";
import { EnvironmentService } from "./environment.service";
import { DevModeService } from "./dev.mode.service";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { LocationService } from "./location.service";
import { InspectionService } from "./inspection.service";
import { ObservationService } from "./observation.service";
import { FormService } from "./form.service";
import { CategoryService } from "./category.service";
import { InspectionTypeService } from "./inspection.type.service";
import { DateService } from "./date.service";

// TODO add preload function that can fetch additional data on download
// TODO add function to save to persistent storage

/**
 * This service provides HS Script functionality to the mobile app.
 */
@Injectable({
  providedIn: "root",
})
export class ScriptService extends BaseService {
  document: Document;

  page: any;

  scriptListeners: any = [];

  scriptServiceKeyMap: any = {
    SearchPage: "touchSearch",
    InspectionPage: "touchInspection",
    ListPage: "touchList",
  };

  constructor(
    protected eventService: EventService,
    protected dateService: DateService,
    private environmentService: EnvironmentService,
    protected networkService: NetworkService,
    protected dataService: DataService,
    protected navigationService: NavigationService,
    protected inspectionService: InspectionService,
    protected formService: FormService,
    protected observationService: ObservationService,
    protected categoryService: CategoryService,
    protected filterService: FilterService,
    protected locationService: LocationService,
    protected inspectionTypeService: InspectionTypeService,
    protected devModeService: DevModeService,
    private scriptRepo: ScriptRepository,
    logService: LogService,
  ) {
    super(logService, "ScriptService");
  }

  public register(pageName: string, page: any) {
    const name = this.scriptServiceKeyMap[pageName];
    if (this.devModeService.isDevMode()) {
      const { scripts } = this.environmentService.getDevVariables();
      if (scripts !== "true") {
        return [];
      }
    }
    if (name) {
      this.page = page;
      this.document = this.page.document || this.page.injectedDocument;
      return this.runScripts(name, "onload");
    }
    return [];
  }

  public updateScripts(pageName: string, page: any) {
    this.clearScripts();
    return this.register(pageName, page);
  }

  public clearScripts() {
    this.scriptListeners.forEach((listener: any) => {
      this.eventService.off(listener.name, listener.id);
    });
    this.filterService.resetFilters();
    this.scriptListeners = [];
  }

  // UI

  /**
   * Returns an HTMLElement's parent node.
   * @param referenceNode The child node to query.
   * @returns The parent node.
   */
  private parentNode(referenceNode: HTMLElement): HTMLElement {
    // For nodes in the light DOM, this will return the actual parent
    let parent = referenceNode.parentNode;

    // Check if the node is a part of a Shadow DOM
    if (
      referenceNode.getRootNode &&
      (referenceNode.getRootNode() as ShadowRoot).mode
    ) {
      // If the node is the host element of a Shadow DOM, the "logical" parent would be the ShadowRoot
      if ((referenceNode.getRootNode() as ShadowRoot).host === referenceNode) {
        parent = referenceNode.getRootNode() as ShadowRoot;
      }
    }
    return parent as HTMLElement;
  }

  /**
   * Returns an HTMLElement's next sibling.
   * @param referenceNode The child node to query.
   * @returns The next sibling or null if there is none.
   */
  private nextSibling(referenceNode: HTMLElement): any {
    // Check if the node is a part of a Shadow DOM
    if (
      referenceNode.getRootNode &&
      (referenceNode.getRootNode() as ShadowRoot).mode
    ) {
      const shadowRoot = referenceNode.getRootNode() as ShadowRoot;

      // If the node is the last child within a Shadow DOM, the "logical" next sibling is the
      // light DOM sibling of the shadow host
      if (referenceNode === shadowRoot.lastElementChild) {
        return shadowRoot.host.nextElementSibling;
      }
    }

    return referenceNode.nextElementSibling;
  }

  /**
   * Creates a new HTMLElement.
   * @param tag The type of element you wish to create.
   * @returns The newly created HTMLElement.
   */
  protected createElement(tag: string): HTMLElement {
    return this.document.createElement(tag);
  }

  /**
   * Creates a comment node.
   * @param value The comment text.
   * @returns The newly created Comment node.
   */
  protected createComment(value: string): Comment {
    return this.document.createComment(value);
  }

  /**
   * Creates a text node.
   * @param value The string to be put in the text node.
   * @returns The newly created Text node.
   */
  protected createText(value: string): Text {
    return this.document.createTextNode(value);
  }

  /**
   * Set a CSS style on a given HTMLElement.
   * @param el The element.
   * @param style The name of the style.
   * @param value The new value.
   * @param priority The priority of the style i.e. "important".
   * @returns The updated HTMLElement.
   */
  protected setStyle(
    el: HTMLElement,
    style: string,
    value: string,
    priority?: string,
  ): HTMLElement {
    el.style.setProperty(style, value, priority);
    return el;
  }

  /**
   * Removes a CSS style on a given HTMLElement.
   * @param el The element.
   * @param style The name of the style to remove.
   * @returns The updated HTMLElement.
   */
  protected removeStyle(el: HTMLElement, style: string): HTMLElement {
    el.style.removeProperty(style);
    return el;
  }

  /**
   * Set a CSS style on a given HTMLElement.
   * @param parent The element.
   * @param newChild The name of the style.
   * @returns The newly appended child.
   */
  protected appendChild(
    parent: HTMLElement,
    newChild: HTMLElement | Text | Comment,
  ): HTMLElement | Text | Comment {
    return parent.appendChild(newChild);
  }

  /**
   * Sets the value of a given HTMLElement.
   * @param el The element.
   * @param value The value you wish to set.
   */
  protected setValue(el: any, value: any): void {
    const elRef = el;
    elRef.value = value;
  }

  /**
   * Sets a property on a given HTMLElement.
   * @param el The element.
   * @param name The name of the property you wish to set.
   * @param value The value you wish to set.
   * @returns The updated HTMLElement.
   */
  protected setProperty(el: any, name: string, value: any): HTMLElement {
    const elCopy = el;
    elCopy[name] = value;
    return el;
  }

  /**
   * Sets an attribute on a given HTMLElement.
   * @param el The element.
   * @param name The name of the attribute you wish to set.
   * @param value The value you wish to set.
   * @returns The updated HTMLElement.
   */
  protected setAttribute(el: HTMLElement, name: string, value: any): void {
    return el.setAttribute(name, value);
  }

  /**
   * Removes an attribute on a given HTMLElement.
   * @param el The element.
   * @param name The name of the attribute you wish to remove.
   */
  protected removeAttribute(el: HTMLElement, name: string): void {
    el.removeAttribute(name);
  }

  /**
   * Adds a CSS class to a given HTMLElement.
   * @param el The element.
   * @param name The name of the class you wish to add.
   */
  protected addClass(el: HTMLElement, name: string): void {
    el.classList.add(name);
  }

  /**
   * Removes a CSS class from a given HTMLElement.
   * @param el The element.
   * @param name The name of the attribute you wish to remove.
   */
  protected removeClass(el: HTMLElement, name: string): void {
    el.classList.remove(name);
  }

  /**
   * add an event listener to a given HTMLElement.
   * @param target The element you wish to listen to.
   * @param eventName The name of the event you want to listen for.
   * @param callback The function to run when the event is emitted.
   */
  protected listen(
    target: HTMLElement,
    eventName: string,
    callback: (event: any) => void,
  ): void {
    target.addEventListener(eventName, callback);
  }

  /**
   * Hides the given element.
   * @param el The element you want to hide.
   */
  protected hide(el: HTMLElement): void {
    this.setStyle(el, "display", "none", "important");
    this.setAttribute(el, "hidden", "true");
  }

  /**
   * Shows the given element if hidden.
   * @param el The element you want to show.
   */
  protected show(el: HTMLElement): void {
    this.removeStyle(el, "display");
    this.removeAttribute(el, "hidden");
  }

  /**
   * Creates a new toast component.
   * @param options An object of options for the toast.
   * @param options.header The header for the toast.
   * @param options.message The message for the toast. Long strings will wrap and the toast container will expand.
   * @param options.color The color to use for the toast.
   * @param options.duration How many milliseconds to wait before hiding the toast. By default, it will show until `dismiss()` is called.
   * @param options.buttons An array of buttons for the toast.
   */
  protected toast(options: {
    header?: string;
    message?: string;
    color?: string;
    duration?: number | undefined;
    buttons?: any[];
  }): void {
    this.page.openToast(
      options.header,
      options.message,
      options.color,
      options.duration,
      options.buttons,
    );
  }

  /**
   * Creates a new alert component.
   *  @param options An object of options for the toast.
   *  @param options.header The title for the alert.
   *  @param options.subHeader The subtitle for the alert.
   *  @param options.message  The message for the alert.
   *  @param options.buttons  An array of buttons for the alert.
   */
  protected alert(options: {
    header?: string;
    subHeader?: string;
    message?: string;
    buttons?: string[];
  }): void {
    this.page.openAlert(
      options.header,
      options.subHeader,
      options.message,
      options.buttons,
    );
  }

  /**
   * Updates an HTMLElement's attributes.
   * @param el The element you want to add attributes to.
   * @param attributes An object of attributes.
   */
  protected updateAttributes(
    el: HTMLElement,
    attributes: { [key: string]: string },
  ) {
    Array.from(el.attributes).forEach((item: any) => {
      this.removeAttribute(el, item.name);
    });
    Object.entries(attributes).forEach(([key, value]) => {
      this.setAttribute(el, key, value);
    });
  }

  /**
   * Updates a HTMLElements CSS classes.
   * @param el The element you want to add classes to.
   * @param classes An array of CSS classes.
   */
  protected updateClasses(el: HTMLElement, classes: string[]) {
    const classesToRemove = el.className.split(" ");
    classesToRemove.forEach((item) => {
      this.removeClass(el, item);
    });
    classes.forEach((item) => {
      this.addClass(el, item);
    });
  }

  /**
   * If an element with the provided ID exists that element will be updated if it does not it will be created.
   * @param options The options for creating or updating the element.
   * @param options.tag The tag of the HTMLElement you want to create.
   * @param options.id The id used identify the HTMLElement, if none is provided this will only act as an insert.
   * @param options.location Where to inject the new element within the DOM.
   * @param options.location.target The target HTMLElement to use as a reference point for the insertion.
   * @param options.location.position The position relative to the target to insert the new element. One of "beforebegin", "afterbegin", "beforeend", "afterend".
   * @param options.text The textContent of the HTMLElement.
   * @param options.value The value you wish to set on the HTMLElement.
   * @param options.styles An array styles you wish to set on the HTMLElement.
   * @param options.classes An array styles you wish to set on the HTMLElement.
   * @param options.attributes An object of key/value pairs that will be added to the HTMLElement as attributes.
   * @returns The newly created or updated HTMLElement, or undefined if an update was attempted but no element was found.
   */
  protected upsertElement(options: {
    tag: string;
    id?: string;
    location?: { target: HTMLElement; position: string };
    text?: string;
    value?: any;
    styles?: { [key: string]: string };
    classes?: string[];
    attributes?: { [key: string]: string };
  }): HTMLElement | undefined {
    const el = this.querySelector(`#${options.id}`);
    if (el) {
      return this.updateElement({ ...options, el });
    }
    return this.insertElement(options);
  }

  /**
   * Adds a button to the navigation tab bar.
   * @param options The options for the button.
   * @param options.label The label for the button.
   * @param options.icon The icon for the button.
   * @param options.id The id used identify the button.
   * @param options.classes An array of classes that get added to the <ion-tab-button> element.
   * @param options.attributes An object of key/value pairs that will be added to the button.
   * @param options.iconAttributes An object of key/value pairs that will be added to the icon.
   * @param options.labelAttributes An object of key/value pairs that will be added to the label.
   * @param options.labelClasses An array of classes that will be added to the label.
   * @returns The newly created or existing button HTMLElement.
   */
  protected addButtonToTabBar(options: {
    label: string;
    icon: string;
    id?: string;
    classes?: string[];
    attributes?: { [key: string]: string };
    iconAttributes: { [key: string]: string };
    labelAttributes?: { [key: string]: string };
    labelClasses?: string[];
  }): HTMLElement {
    if (options.id) {
      const el = this.querySelector(`#${options.id}`);
      if (el) {
        return el;
      }
    }

    const btnEl = this.insertElement({
      location: {
        target: this.querySelector("ion-tab-bar"),
        position: "append",
      },
      tag: "ion-tab-button",
      id: options.id,
      attributes: options.attributes,
      classes: options.classes,
    });

    this.insertElement({
      location: { target: btnEl, position: "append" },
      tag: "ion-icon",
      attributes: { ...options.iconAttributes, name: options.icon },
    });

    this.insertElement({
      location: { target: btnEl, position: "append" },
      tag: "ion-label",
      text: options.label,
      classes: options.labelClasses,
    });

    return btnEl;
  }

  /**
   * If an HTMLElement is provided it will return it, else it will query the dom with given selector for an HTMLElement.
   * @param selector A selector to find the desired HTMLElement.
   * @param el The element you wish to return.
   * @returns The found HTMLElement or undefined if no element was found.
   */
  protected useSelectorOrProvidedEl(
    selector: string | undefined,
    el: HTMLElement | undefined,
  ): HTMLElement | undefined {
    return selector && !el ? this.querySelector(selector) : el;
  }

  /**
   * Updates an HTMLElement
   * @param options The options for updating the element.
   * @param options.el The HTMLElement you wish to update.
   * @param options.selector The selector used identify the HTMLElement, an alternative option if you don't wish to provide an HTMLElement.
   * @param options.text The textContent of the HTMLElement.
   * @param options.value The value you wish to set on the HTMLElement.
   * @param options.styles An array styles you wish to set on the HTMLElement.
   * @param options.classes An array styles you wish to set on the HTMLElement.
   * @param options.attributes An object of key/value pairs that will be added to the HTMLElement as attributes.
   * @returns The updated HTMLElement or undefined if no element was found.
   */
  protected updateElement(options: {
    el?: HTMLElement;
    selector?: string;
    text?: string;
    value?: any;
    styles?: { [key: string]: string };
    classes?: string[];
    attributes?: { [key: string]: string };
  }): HTMLElement | undefined {
    const el = this.useSelectorOrProvidedEl(options.selector, options.el);
    if (el) {
      if (options.text) {
        this.setProperty(el, "textContent", options.text);
      }
      if (options.value !== undefined) {
        this.setValue(el, options.value);
      }
      if (options.styles !== undefined) {
        this.setStyles(el, options.styles);
      }
      if (options.attributes) {
        this.updateAttributes(el, options.attributes);
      }
      if (options.classes) {
        this.updateClasses(el, options.classes);
      }
    }
    return el;
  }

  /**
   * Sets a variable number of styles on a given HTMLElement.
   * @param el The element you wish to set styles for.
   * @param styles A two dimensional array consisting of the key/value for provides styles.
   */
  protected setStyles(
    el: HTMLElement,
    styles: { [key: string]: string },
  ): void {
    Object.entries(styles).forEach(([key, value]) => {
      this.setStyle(el, key, value);
    });
  }

  /**
   * Creates an HTMLElement and inserts it into the DOM.
   * @param options The options for creating the element.
   * @param options.tag The tag of the HTMLElement you want to create.
   * @param options.id The id used identify the HTMLElement.
   * @param options.location Where to inject the new element within the DOM.
   * @param options.location.target The target HTMLElement to use as a reference point for the insertion.
   * @param options.location.position The position relative to the target to insert the new element. One of "beforebegin", "afterbegin", "beforeend", "afterend".
   * @param options.text The textContent of the HTMLElement.
   * @param options.value The value you wish to set on the HTMLElement.
   * @param options.styles An array styles you wish to set on the HTMLElement.
   * @param options.classes An array styles you wish to set on the HTMLElement.
   * @param options.attributes An object of key/value pairs that will be added to the HTMLElement as attributes.
   * @returns The newly created HTMLElement.
   */
  protected insertElement(options: {
    tag: string;
    id?: string;
    location?: { target: HTMLElement; position: string };
    text?: string;
    value?: any;
    styles?: { [key: string]: string };
    classes?: string[];
    attributes?: { [key: string]: string };
  }): HTMLElement {
    const newEl = this.createElement(options.tag);
    if (options.id) {
      this.setAttribute(newEl, "id", options.id);
    }
    if (options.text) {
      const newText = this.createText(options.text);
      this.appendChild(newEl, newText);
    }

    if (options.value) {
      this.setProperty(newEl, "value", options.value);
    }

    if (options.styles) {
      this.setStyles(newEl, options.styles);
    }

    if (options.attributes) {
      Object.entries(options.attributes).forEach(([key, value]) => {
        this.setAttribute(newEl, key, value);
      });
    }

    if (options.classes) {
      options.classes.forEach((item) => {
        this.addClass(newEl, item);
      });
    }

    if (options?.location?.target) {
      if (options.location.position === "before") {
        this.insertBefore(newEl, options.location.target);
      } else if (options.location.position === "after") {
        this.insertAfter(newEl, options.location.target);
      } else if (options.location.position === "append") {
        this.appendChild(options.location.target, newEl);
      } else {
        this.insertAfter(newEl, options.location.target);
      }
    }
    return newEl;
  }

  /**
   * Removes a HTMLElement from the DOM.
   * @param el The new HTMLElement you wish to remove.
   */
  protected removeElement(el: HTMLElement): void {
    const parent = this.parentNode(el);
    parent.removeChild(el);
  }

  /**
   * Inserts a HTMLElement after the provided reference node.
   * @param newNode The new HTMLElement you wish to insert.
   * @param referenceNode The HTMLElement you wish to place your new HTMLElement after.
   * @returns The inserted node.
   */
  protected insertAfter(newNode: HTMLElement, referenceNode: HTMLElement): any {
    if (this.nextSibling(referenceNode)) {
      return this.nextSibling(referenceNode).before(newNode);
    }
    return this.parentNode(referenceNode).appendChild(newNode);
  }

  /**
   * Inserts a HTMLElement before the provided reference node, assumes parent node for convenience.
   * @param newNode The new HTMLElement you wish to insert.
   * @param referenceNode The HTMLElement you wish to place your new HTMLElement after.
   */
  protected insertBefore(
    newNode: HTMLElement,
    referenceNode: HTMLElement,
  ): void {
    referenceNode.before(newNode);
  }

  private isInShadowDOM(elem: Node): boolean {
    return elem.getRootNode() instanceof ShadowRoot;
  }

  protected closest(elem: Element | null, selector: string): Element | null {
    if (!elem) {
      return null;
    }

    // Base case: If the current element matches the selector, return it
    if (elem.matches(selector)) {
      return elem;
    }

    // Decide the next element to check: either the shadow host (if in shadow DOM) or the parent
    const nextElem = this.isInShadowDOM(elem)
      ? (elem.getRootNode() as ShadowRoot).host
      : elem.parentElement;

    // Recursive case: Move to the next element and check again
    return this.closest(nextElem, selector);
  }

  /**
   * Returns an Element object representing the element whose id property matches the specified string.
   * @param id The id of the element you wish to get.
   * @returns The HTMLElement with the provided id.
   */
  protected getElementById(id: string): HTMLElement {
    return <HTMLElement>querySelectorDeep(`#${id}`, this.document);
  }

  /**
   * Returns the first element that is a descendant of node that matches selectors.
   * @param selector The selector you wish to match on.
   * @returns The first HTMLElement that matches the selector.
   */
  protected querySelector(selector: string): HTMLElement {
    return <HTMLElement>querySelectorDeep(selector, this.document);
  }

  /**
   * Waits for a HTMLElement to exist within the the DOM, once it does it executes a callback.
   * @param selector The selector you wish to match on.
   * @param callback The function you wish to execute once the element exists.
   */
  protected waitForElement(
    selector: string,
    callback: (el: HTMLElement) => void,
  ): void {
    if (this.querySelector(selector)) {
      callback(this.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (this.querySelector(selector)) {
        observer.disconnect();
        callback(this.querySelector(selector));
      }
    });

    observer.observe(this.document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Returns all element descendants of node that match selectors.
   * @param selector The selector you wish to match on.
   * @returns An array of HTMLElements that match the selector.
   */
  protected querySelectorAll(selector: string): HTMLElement[] {
    return querySelectorAllDeep(selector, this.document);
  }

  // DATA

  /**
   * Accesses a property of the given view page
   * @param key The desired property name.
   */
  protected setVar(key: string, value: any): void {
    if (isSignal(this.page[key])) {
      const variable: any = this.page[key];
      variable.set(value);
    } else {
      this.page[key] = value;
    }
  }

  /**
   * Accesses a property of the given view page
   * @param key The desired property name.
   * @returns The value of the desired property.
   */
  protected getVar(key: string): any {
    const inspectionFormMap: any = {
      locationFormFields: "locationInfo",
      generalFormFields: "generalInfo",
    };
    const inspectionForms = ["generalInfo", "locationInfo"];
    const inspectionFormFields = Object.keys(inspectionFormMap);
    if (inspectionFormFields.includes(key)) {
      const form = this.formService.getForm(inspectionFormMap[key]);
      return form.fields;
    }
    if (inspectionForms.includes(key)) {
      const form = this.formService.getForm(key);
      return form.state;
    }
    if (isSignal(this.page[key])) {
      return this.page[key]();
    }
    return this.page[key];
  }

  /**
   * Retrieves a schema/table from the persistent storage.
   * @param key The desired schema name.
   * @returns The schema/table object.
   */
  protected getSchema(key: string): any {
    return this.dataService.getSchema(key);
  }

  // NAVIGATION

  /**
   * Takes user to the desired view.
   * @param desiredView The view to send user to.
   */
  protected async setPageView(desiredView: string): Promise<void> {
    await this.navigationService.navigate(desiredView);
  }

  /**
   * Takes user to the desired view.
   * @param desiredView The view to send user to.
   */
  protected setInspectionView(desiredView: string): void {
    this.setVar("viewLoaded", desiredView);
  }

  /**
   * Takes the user back to the previous view.
   */
  protected async goBack(): Promise<void> {
    await this.navigationService.back();
  }

  // EVENTS

  /**
   * listens for named event and executes given function.
   * @param name The name on the event you wish to listen for.
   * @param id A unique identifier for your given function.
   * @param func Function to execute on form change.
   */
  protected onEvent(
    name: string,
    id: string,
    func: (...args: any[]) => void,
  ): void {
    this.eventService.on(name, id, func);
    this.scriptListeners.push({ name, id });
  }

  /**
   * Emits a named event.
   * @param name The name of the event you wish to emit.
   * @param args Arguments you wish to provide.
   */
  protected emitEvent(name: string, ...args: any[]): void {
    this.eventService.emit(name, args);
  }

  /**
   * listens for changes on a given field.
   * @param formGroup The form you wish to listen to.
   * @param fieldName The field you wish to listen to.
   * @param func Function to execute on field change.
   * @returns The subscription created, can be used to unsubscribe if needed.
   */
  protected onFieldValueChange(
    formGroup: FormGroup,
    fieldName: string,
    func: (value: any) => void,
  ) {
    const sub = formGroup?.get(fieldName)?.valueChanges.subscribe(func);
    if (sub) {
      this.formService.addSubscription(formGroup, sub);
    }
    return sub;
  }

  /**
   * listens for changes on the form.
   * @param formGroup The form you wish to listen to.
   * @param func Function to execute on form change.
   * @returns The subscription created, can be used to unsubscribe if needed.
   */
  protected onFormValueChange(
    formGroup: FormGroup,
    func: (value: any) => void,
  ) {
    const sub = formGroup.valueChanges.subscribe(func);
    this.formService.addSubscription(formGroup, sub);
    return sub;
  }

  // OPERATIONAL

  /**
   * Converts a string to a safe html string.
   * @param html The html string you wish to convert.
   * @returns {SafeHtml} The safe html string.
   */
  private createSafeHTML(html: string): SafeHtml {
    return this.page.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Converts a string to a safe css string.
   * @param css The css string you wish to convert.
   * @returns {SafeStyle} The safe css string.
   */
  private createSafeCSS(css: string): SafeStyle {
    return this.page.sanitizer.bypassSecurityTrustStyle(css);
  }

  /**
   * Checks whether the application is currently online.
   * @returns {boolean} online status, if true the application is currently online.
   */
  protected checkConnectivity(): boolean {
    return this.networkService.getCurrentStatus();
  }

  /**
   * Checks whether the application is currently online.
   * @returns {boolean} online status, if true the application is currently online.
   */
  protected getAppVersion(): string {
    return this.environmentService.getVersion();
  }

  /**
   * Checks whether the application is currently online.
   * @returns {boolean} online status, if true the application is currently online.
   */
  protected isDevMode(): boolean {
    return this.devModeService.isDevMode();
  }

  /**
   * Checks whether the application is currently online.
   * @returns {boolean} online status, if true the application is currently online.
   */
  protected isCompatibleVersion(requiredVersion: string): boolean {
    const currentVersion = this.environmentService.getVersion();

    if (requiredVersion.startsWith("^")) {
      return satisfies(currentVersion, requiredVersion);
    }

    // If required version specifies a major, minor, and patch version
    if (requiredVersion.split(".").length === 3) {
      return compare(currentVersion, requiredVersion, "=");
    }

    // If required version specifies only major and minor versions
    if (requiredVersion.split(".").length === 2) {
      const currentMajorMinor = currentVersion.split(".").slice(0, 2).join(".");
      return compare(requiredVersion, currentMajorMinor, "=");
    }

    // If only the major version is specified
    return compare(requiredVersion, currentVersion, "=");
  }

  /**
   * Provides the API for use by HS scripts.
   * @returns An object containing the API.
   */
  protected provideApi(): any {
    return {
      date: this.dateService,
      ui: {
        closest: (elem: Element | null, selector: string) =>
          this.closest(elem, selector),
        parentNode: (el: any) => this.parentNode(el),
        nextSibling: (el: any) => this.nextSibling(el),
        createElement: (type: string) => this.createElement(type),
        createComment: (text: string) => this.createComment(text),
        createText: (text: string) => this.createText(text),
        setStyle: (el: any, style: string, value: string) =>
          this.setStyle(el, style, value),
        removeStyle: (el: any, style: string) => this.removeStyle(el, style),
        appendChild: (el: any, child: any) => this.appendChild(el, child),
        setValue: (el: any, value: any) => this.setValue(el, value),
        setProperty: (el: any, name: string, value: any) =>
          this.setProperty(el, name, value),
        setAttribute: (el: any, attr: string, value: any) =>
          this.setAttribute(el, attr, value),
        removeAttribute: (el: any, name: string) =>
          this.removeAttribute(el, name),
        addClass: (el: any, name: string) => this.addClass(el, name),
        removeClass: (el: any, name: string) => this.removeClass(el, name),
        listen: (target: any, eventName: string, callback: () => void) =>
          this.listen(target, eventName, callback),
        alert: (options: any) => this.alert(options),
        toast: (options: any) => this.toast(options),
        waitForElement: (selector: string, callback: any) =>
          this.waitForElement(selector, callback),
        getElementById: (elementID: string) => this.getElementById(elementID),
        querySelector: (selector: string) => this.querySelector(selector),
        querySelectorAll: (selector: string) => this.querySelectorAll(selector),
        hide: (el: any) => this.hide(el),
        show: (el: any) => this.show(el),
        insertBefore: (newNode: any, referenceNode: any) =>
          this.insertBefore(newNode, referenceNode),
        insertAfter: (newNode: any, referenceNode: any) =>
          this.insertAfter(newNode, referenceNode),
        insertElement: (options: any) => this.insertElement(options),
        removeElement: (el: any) => this.removeElement(el),
        updateElement: (options: any) => this.updateElement(options),
        upsertElement: (options: any) => this.upsertElement(options),
        addButtonToTabBar: (options: any) => this.addButtonToTabBar(options),
      },
      events: {
        onEvent: (name: string, eventID: string, func: any) =>
          this.onEvent(name, eventID, func),
        emitEvent: (name: string, ...args: any) => this.emitEvent(name, args),
        onFieldValueChange: (
          formGroup: FormGroup,
          fieldName: string,
          func: any,
        ) => this.onFieldValueChange(formGroup, fieldName, func),
        onFormValueChange: (formGroup: FormGroup, func: any) =>
          this.onFormValueChange(formGroup, func),
      },
      nav: {
        goBack: () => this.goBack(),
        setView: (view: string) => this.setInspectionView(view),
        setPageView: (view: string) => this.setPageView(view),
      },
      data: {
        setVar: (key: string, value: any) => this.setVar(key, value),
        getVar: (key: string) => this.getVar(key),
        getSchema: (key: string) => this.getSchema(key),
      },
      utils: {
        getAppVersion: () => this.getAppVersion(),
        isCompatibleVersion: (requiredVersion: string) =>
          this.isCompatibleVersion(requiredVersion),
        checkConnectivity: () => this.checkConnectivity(),
        sortByDateProperty:
          (key: string, order: string = "DESC") =>
          (a: any, b: any) =>
            order === "DESC"
              ? +new Date(b[key]) - +new Date(a[key])
              : +new Date(a[key]) - +new Date(b[key]),
        guid: () => uuid(),
        createSafeHTML: (html: string) => this.createSafeHTML(html),
        createSafeCSS: (css: string) => this.createSafeCSS(css),
        isDevMode: () => this.isDevMode(),
      },
    };
  }

  /**
   * Executes the desired scripts with the provided API.
   * @param runWhere Where the script should execute.
   * @param runWhen When the script should execute.
   * @returns An array of results from the executed scripts.
   */
  public runScripts(runWhere: string, runWhen: string) {
    const touchScripts = this.scriptRepo.getFilteredByValues(
      ["runWhere", "runWhen", "active"],
      [runWhere, runWhen, "1"],
    );
    const hs = this.provideApi();
    if (touchScripts.length) {
      console.warn(
        "The following scripts running on this page:",
        touchScripts.map((script) => script.name),
      );
    }
    return touchScripts.reduce(
      async (acc: any, script: { name: string; code: any }) => {
        const awaitedAcc = await acc;
        try {
          // eslint-disable-next-line @typescript-eslint/no-implied-eval
          const generatedFunction = Function(
            `return async (hs,document,logger)=>{try{${script.code}}catch(error){logger.error("Error evaluating script: ${script.name}",error)}};`,
          )();
          const res = await generatedFunction(hs, this.document, this.logger);
          return [...awaitedAcc, res];
        } catch (err) {
          this.logger.error(`Error evaluating script: ${script.name}`, err);
          return awaitedAcc;
        }
      },
      [],
    );
  }
}
